import { faQuestionCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconButton } from '@mui/material';
import classNames from 'classnames';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import EmptyAutoAssign from 'src/assets/empty-auto-assign.svg';
import Avatar from 'src/components/ui/Avatar/Avatar';
import Button from 'src/components/ui/Button/Button';
import EmptyState from 'src/components/ui/EmptyState/EmptyState';
import PageHeader from 'src/components/ui/PageHeader/PageHeader';
import Table from 'src/components/ui/Table/Table';
import {
  ServiceAccountTableQueryParameters,
  TableHeader,
} from 'src/components/ui/Table/Table.interface';
import TablePagination from 'src/components/ui/Table/TablePagination/TablePagination';
import { paginationDefaultState } from 'src/constants/paginationConstants';
import { addModal } from 'src/features/globalSlice';
import { routes } from 'src/routes';
import {
  bulkEditAutoAssign,
  getAutoAssignServiceAccountList,
  setAutoAssignServiceAccountList,
} from 'src/store/actions/serviceAccountActions';
import { ModalType } from 'src/store/models/Modal';
import { ServiceAccount } from 'src/store/models/ServiceAccount';
import { useLoadingSelector } from 'src/utils/useLoadingSelector';
import { useQueryParams } from 'src/utils/useQueryParams';

import classes from './AutoAssignServiceAccounts.module.scss';

const AutoAssignServiceAccounts: FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const serviceAccountList = useAppSelector(
    (state) => state.serviceAccount.autoAssignList
  );
  const loading = useLoadingSelector({
    actionType: getAutoAssignServiceAccountList.toString(),
  });

  const headers: TableHeader<ServiceAccount>[] = [
    {
      accessor: 'title',
      label: t('common:name'),
      sortable: true,
      component: (account) => (
        <Avatar
          title={account?.title}
          image={account?.image}
          imageSize={32}
          className={classes.Avatar}
        />
      ),
    },
    {
      accessor: 'company',
      label: t('common:company'),
      sortable: true,
      sortKey: 'company.title',
      component: (account) => account?.company?.title,
    },
    {
      accessor: 'eMail',
      label: t('common:email'),
      sortable: true,
    },
    {
      accessor: 'activeTicketCount',
      label: t('service_account:active_tickets'),
      sortable: true,
    },
  ];

  const { urlParams } = useQueryParams<ServiceAccountTableQueryParameters>(
    location.search,
    {
      ...paginationDefaultState,
    }
  );

  useEffect(() => {
    dispatch(
      setAutoAssignServiceAccountList({ count: 0, data: [], ctx: null })
    );
    return () => {
      dispatch(
        setAutoAssignServiceAccountList({ count: 0, data: [], ctx: null })
      );
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    dispatch(
      getAutoAssignServiceAccountList({
        params: urlParams,
      })
    );
    // eslint-disable-next-line
  }, [location]);

  const endNode = (
    <div className={classes.HeaderEnd}>
      <IconButton
        onClick={() =>
          dispatch(
            addModal({
              title: t('service_account:auto_assign_list'),
              modalType: ModalType.BASIC,
              body: t('service_account:auto_assign_info'),
            })
          )
        }
      >
        <FontAwesomeIcon icon={faQuestionCircle} />
      </IconButton>
      <Button
        onClick={() =>
          dispatch(
            addModal({
              title: t('common:are_you_sure'),
              modalType: ModalType.AUTO_ASSIGN,
              loadingAction: bulkEditAutoAssign.type,
            })
          )
        }
        className={classes.HeaderButton}
      >
        {t('service_account:edit_auto_assign')}
      </Button>
    </div>
  );

  return (
    <div className={classes.Container}>
      <PageHeader title={t('service_account:auto_assign')} endNode={endNode} />
      <Table
        headers={headers}
        data={serviceAccountList?.data}
        search
        loading={loading}
        className={classNames(classes.Table, { [classes.Loading]: loading })}
        classes={{ header: classes.TableHeader }}
        onRowClick={(row) =>
          navigate(`${routes.EDIT_SERVICE_ACCOUNT}/${row?.id}`)
        }
        emptyTableComponent={
          <EmptyState
            title={t('service_account:empty_auto_assign')}
            subtitle={t('service_account:empty_auto_assign_details')}
            icon={EmptyAutoAssign}
          />
        }
        searchPlaceholder={t('common:search_field')}
      />
      <TablePagination
        className={classes.Pagination}
        urlParams={urlParams}
        total={serviceAccountList?.count}
      />
    </div>
  );
};

export default AutoAssignServiceAccounts;
